import { useState, FormEvent } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { FormField, Input } from '@amzn/awsui-components-react';
import TopHeader from '../components/TopHeader';
import Main from '../components/Main';
import Footer from '../components/Footer';
import GandalfFlashbar from '../components/GandalfFlashbar';
import usePageTitle from '../hooks/usePageTitle';
import { useSignInPageSetup } from '../hooks/useSignInPageSetup';
import useSelfJoinCheck from '../hooks/useSelfJoinCheck';
import { AppConfig } from '../types/app';
import { validateEmail } from '../utils/email';
import messages from '../i18n/join.messages';
import signInMessages from '../i18n/simplifiedSignIn.messages';
import styles from './SimplifiedSignIn.module.css';
import joinStyles from './JoinPage.module.css';

// Learner landing page for a shareable invite link (/join/:organizationId).
function JoinPage({ config }: { config: AppConfig }) {
  usePageTitle();
  const { formatMessage } = useIntl();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { enableCookieComponent } = useSignInPageSetup(config);

  // Feature off: skip the check, so the page shows "not active" like an inactive link.
  const joinEnabled = config.enableShareableInviteLink === 'true';
  const {
    isLoading: isChecking,
    isActive,
    orgName,
  } = useSelfJoinCheck(config, joinEnabled ? organizationId : undefined);

  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);

  // Signing in from this page comes in ORDERING-5505; for now Continue only
  // validates the email.
  const handleContinue = (e: FormEvent) => {
    e.preventDefault();
    setIsValidEmail(validateEmail(email));
  };

  const renderPage = (
    content?: React.ReactNode,
    pageClassName = joinStyles.page
  ) => (
    <div className={pageClassName}>
      <div className={styles.stickyHeader}>
        <TopHeader config={config} />
      </div>
      <div className={styles.mainContent}>
        <Main config={config}>
          <div className={joinStyles.content}>{content}</div>
        </Main>
      </div>
      <Footer enableCookieComponent={enableCookieComponent} />
    </div>
  );

  if (isChecking) {
    return renderPage();
  }

  // A failed check looks the same as an inactive link, so the page never
  // reveals why.
  if (!isActive) {
    return renderPage(
      <>
        <GandalfFlashbar />
        <div className={joinStyles.errorContainer}>
          <div className={joinStyles.errorCard}>
            <h1 className={joinStyles.errorHeading}>
              {formatMessage(messages.linkNotActive)}
            </h1>
            <p className={joinStyles.errorBody}>
              {formatMessage(messages.linkNotActiveBody)}
            </p>
          </div>
        </div>
      </>,
      joinStyles.errorPage
    );
  }

  return renderPage(
    <>
      <GandalfFlashbar />
      <div className={joinStyles.container}>
        <div className={joinStyles.card}>
          <h1 className={styles.header}>
            {formatMessage(messages.header, { organizationName: orgName })}
          </h1>
          <p className={joinStyles.subtitle}>
            {formatMessage(messages.subtitle, { organizationName: orgName })}
          </p>
          <form onSubmit={handleContinue} noValidate>
            <FormField
              errorText={
                !isValidEmail ? formatMessage(signInMessages.emailInvalid) : ''
              }
              i18nStrings={{ errorIconAriaLabel: '' }}
            >
              <span className={styles.inputLabel}>
                {formatMessage(messages.emailLabel)}
              </span>
              <p id="join-email-helper" className={joinStyles.helperText}>
                {formatMessage(messages.emailHelper)}
              </p>
              <Input
                type="email"
                value={email}
                ariaLabel={formatMessage(messages.emailLabel)}
                ariaDescribedby="join-email-helper join-email-footnote"
                placeholder={formatMessage(messages.emailPlaceholder)}
                onChange={({ detail }) => {
                  setEmail(detail.value);
                  setIsValidEmail(true);
                }}
                data-testid="join-email-input"
              />
              <p
                id="join-email-footnote"
                className={joinStyles.helperTextBelow}
              >
                {formatMessage(messages.emailFootnote)}
              </p>
            </FormField>
            <button
              type="submit"
              className={joinStyles.primaryButton}
              data-testid="join-continue-button"
            >
              {formatMessage(signInMessages.continueButton)}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default JoinPage;
